import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getCurrencyWarningsApi } from '@flipster/apis/v1/currency';
import { postConversionInitiateApi } from '@flipster/apis/v1/wallet';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { useSendApi } from '@flipster/react/misc/use-send-api';
import { css, cx } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { ControlSelectBox, DefaultNumberInputMaxLength, Price } from '@flipster/ui';
import { alert } from '@flipster/ui/Alert';
import SendIcon from '@flipster/ui/assets/svgs/send.svg';
import Num, { BigNumber } from '@flipster/utils/num';

import { CurrencyDisplay } from '../../../../../misc/display';
import { useSnapshotSpec } from '../../../../snapshot/use-snapshot-spec';
import { CurrencyWarningAlert } from '../../../shared/alert/currency-warning';
import { walletModalBunja } from '../../../shared/core/state';
import { calcAmountRatio, calcFromAmount, calcToAmount } from '../../shared/calculate';
import { ConvertFormCustomError, ConvertFormSchema } from '../../shared/validation';
import { convertModalBunja } from '../../state';
import { AmountInput } from './amount-input';
import { ConversionLimitInfo } from './conversion-limit-info';
import { ConvertibleTooltip } from './convertible-tooltip';
import { ExchangeRate } from './exchange-rate';
import { RatioButtons } from './ratio-buttons';

const SetValueOptions = { shouldDirty: true };

// from amount, to amount 내림처리
export const ConvertForm = () => {
  const { close } = useOverlayItem();
  const t = useTranslations();
  const {
    control,
    setValue,
    getValues,
    trigger,
    handleSubmit,
    formState: { errors, dirtyFields, isValid },
  } = useFormContext<ConvertFormSchema>();

  // FIXME: use bunja from/to currency instead of rhf
  const { toCurrencyAtom } = useBunja(convertModalBunja);
  const setToCurrency = useSetAtom(toCurrencyAtom);
  const { fromCurrency, toCurrency } = useWatch({ control });
  const [focusInput, setFocusInput] = useState<'fromAmount' | 'toAmount'>('fromAmount');

  const {
    options: { defaultValues },
  } = useBunja(walletModalBunja);
  const {
    steps: { pushAtom },
    precisionAtom,
    toItemsAtom,
    convertibleLimitAtom,
    convertibleLimitBalanceAtom,
    convertibleLimitCurrencyAtom,
    fromMinAmountAtom,
    exchangeRateAtom,
    walletBalanceWithdrawableAtom,
  } = useBunja(convertModalBunja);

  const push = useSetAtom(pushAtom);
  const { from: fromPrecision, to: toPrecision } = useAtomValue(precisionAtom);
  const toItems = useAtomValue(toItemsAtom);
  const convertibleLimit = useAtomValue(convertibleLimitAtom);
  const convertibleLimitBalance = useAtomValue(convertibleLimitBalanceAtom);
  const convertibleLimitCurrency = useAtomValue(convertibleLimitCurrencyAtom);
  const fromMinAmount = useAtomValue(fromMinAmountAtom);
  const exchangeRate = useAtomValue(exchangeRateAtom);
  const walletBalanceWithdrawable = useAtomValue(walletBalanceWithdrawableAtom);

  const { currenciesKeyByCurrency } = useSnapshotSpec();
  const convertiblePrecision = currenciesKeyByCurrency?.[convertibleLimitCurrency || '']?.fullDisplayPrecision ?? 8;
  const convertibleBalance =
    convertibleLimitCurrency === fromCurrency
      ? convertibleLimitBalance?.fromCurrency
      : convertibleLimitBalance?.toCurrency;

  const isDirty = dirtyFields.fromAmount && dirtyFields.toAmount;

  const currentPageWarningQuery = useQuery({
    queryKey: ['getCurrencyWarnings'],
    queryFn: getCurrencyWarningsApi,
    select: ({ data }) =>
      data.warnings.find(
        (warning) =>
          warning.type === 'CURRENCY_WARNING_TYPE_CONVERSION' &&
          (warning.currency.toLowerCase() === fromCurrency?.toLowerCase() ||
            warning.currency.toLowerCase() === toCurrency?.toLowerCase()),
      ),
  });

  const onSelectToCurrency = useCallback(
    (value: string | undefined) => {
      if (!value) return;
      setValue('fromAmount', ''); // reset 사용하지 않은 이유는 reset 은 뎁스마다 control value 의 값이 불일치 (이슈)
      setValue('toAmount', '');
    },
    [setValue],
  );

  const triggerFromAmount = useCallback(() => {
    const newToAmount = getValues('toAmount');
    if (!newToAmount || !exchangeRate) return;
    setValue(
      'fromAmount',
      calcFromAmount(newToAmount, exchangeRate, fromPrecision, DefaultNumberInputMaxLength),
      SetValueOptions,
    );
    trigger(['fromAmount', 'toAmount']);
  }, [exchangeRate, fromPrecision, getValues, setValue, trigger]);

  const triggerToAmount = useCallback(
    (fromAmount?: string) => {
      const newFromAmount = fromAmount || getValues('fromAmount');
      if (!newFromAmount || !exchangeRate) return;
      setValue(
        'toAmount',
        calcToAmount(newFromAmount, exchangeRate, toPrecision, DefaultNumberInputMaxLength),
        SetValueOptions,
      );
      trigger(['fromAmount', 'toAmount']);
    },
    [exchangeRate, getValues, setValue, toPrecision, trigger],
  );

  const setFormAmountPercent = useCallback(
    (percent: string) => {
      if (!convertibleLimitBalance || !convertibleLimitCurrency || !exchangeRate) return;

      if (convertibleLimitCurrency === fromCurrency) setFocusInput('toAmount');
      else setFocusInput('fromAmount');

      const fromAmount = calcAmountRatio(
        convertibleLimitBalance.fromCurrency,
        percent,
        fromPrecision,
        DefaultNumberInputMaxLength,
      );

      setValue('fromAmount', fromAmount, SetValueOptions);
      const toAmount = calcAmountRatio(
        convertibleLimitBalance.toCurrency,
        percent,
        toPrecision,
        DefaultNumberInputMaxLength,
      );

      setValue('toAmount', toAmount, SetValueOptions);
      trigger(['fromAmount', 'toAmount']);
    },
    [
      convertibleLimitBalance,
      convertibleLimitCurrency,
      exchangeRate,
      fromCurrency,
      fromPrecision,
      setValue,
      toPrecision,
      trigger,
    ],
  );

  const [isLoading, onSubmit] = useSendApi(async (formValues: ConvertFormSchema) => {
    try {
      setFocusInput('fromAmount');
      const res = await postConversionInitiateApi({
        fromCurrency: formValues.fromCurrency,
        fromAmount: formValues.fromAmount,
        toCurrency: formValues.toCurrency,
        currencyConversionType: 'CURRENCY_CONVERSION_TYPE_V2',
      });
      setValue('currencyConversionId', res.data.currencyConversionId);
      setValue('initFromAmount', res.data.fromAmount);
      setValue('initToAmount', res.data.toAmount);
      setValue('initExchangeRate', res.data.exchangeRate);
      push({ step: 'preview', context: undefined });
    } catch (err) {
      alert.error(err);
    }
  });

  useEffect(() => {
    // FIXME: adhoc for rhf <-> jotai
    if (toCurrency) setToCurrency(toCurrency);
    if (!toCurrency) {
      setValue('toCurrency', toItems[0]?.value, { shouldValidate: true });
      setToCurrency(toItems[0]?.value);
    }
  }, [setValue, setToCurrency, toCurrency, toItems]);

  useEffect(() => {
    // defaultConvertAmountPercent 값이 있고, dirty 가 아닐때만 실행
    if (defaultValues.percent && !isDirty) {
      setFormAmountPercent(defaultValues.percent);
    }
  }, [defaultValues.percent, isDirty, setFormAmountPercent]);

  useEffect(() => {
    // validation 값 셋팅
    setValue('fromMinAmount', fromMinAmount);
    setValue('fromMaxAmount', walletBalanceWithdrawable);
    if (convertibleLimitBalance?.toCurrency)
      setValue(
        'toCurrencyAvailableBalance',
        Num(convertibleLimitBalance.toCurrency).toFixed(toPrecision, BigNumber.ROUND_DOWN),
      );
  }, [setValue, convertibleLimitBalance?.toCurrency, fromMinAmount, walletBalanceWithdrawable, toPrecision]);

  useEffect(() => {
    if (focusInput === 'fromAmount') triggerToAmount();
  }, [focusInput, triggerToAmount]);

  useEffect(() => {
    if (focusInput === 'toAmount') triggerFromAmount();
  }, [focusInput, triggerFromAmount]);

  const errorMessage = useMemo(() => {
    if (errors.fromAmount) {
      const msg = errors.fromAmount.message;
      switch (msg) {
        case ConvertFormCustomError.NO_AVAILABLE_ASSET:
          return <FormattedMessage id='Convert.r250401.No_available_asset' />;
        case ConvertFormCustomError.CONVERTIBLE_BALANCE_EXCEEDED:
          return <FormattedMessage id='Convert.r250401.Convertible_balance_has_been_exceeded' />;
        case ConvertFormCustomError.MININUM_CONVERSION_AMOUNT:
          return (
            <FormattedMessage
              id='Convert.r250401.The_minimum_conversion_amout_is_amount_symbol'
              values={{
                amount: fromMinAmount,
                symbol: fromCurrency,
              }}
            />
          );
        default:
          return undefined;
      }
    }
    if (errors.toAmount?.message === ConvertFormCustomError.AVAILABLE_BALANCE_EXCEEDED) {
      return <FormattedMessage id='Convert.r250401.Available_balance_has_been_exceeded' />;
    }
    return undefined;
  }, [errors.fromAmount, errors.toAmount, fromMinAmount, fromCurrency]);

  return (
    <>
      <form className={css({ color: 'gray.900' })} onSubmit={handleSubmit(onSubmit)}>
        <div
          className={css({
            pb: 12,
            mb: 30,
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderColor: 'legacy.gray.4',
          })}
        >
          <ConvertibleTooltip />
          <Price
            fontType='numTitle'
            price={convertibleBalance}
            precision={convertiblePrecision}
            currency={convertibleLimitCurrency}
            toFixed
          />
        </div>
        <div className={cx(rowStyle, labelStyle)}>
          <p>
            <FormattedMessage id='Convert.r250401.From' />
          </p>
          <p className={css({ textAlign: 'right' })}>
            <FormattedMessage id='Deposit.r250401.To' />
          </p>
        </div>
        <div className={rowStyle}>
          <div
            className={flex({
              justify: 'center',
              align: 'center',
              flex: 1,
              bg: 'legacy.gray.1',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'legacy.gray.4',
              borderRadius: 4,
            })}
          >
            <CurrencyDisplay
              currency={fromCurrency!}
              icon={<CurrencyDisplay.Icon size={24} space={8} />}
              mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/M/Strong' }} />}
            />
          </div>
          <span className={arrowStyle}>
            <SendIcon />
          </span>
          <ControlSelectBox
            name='toCurrency'
            shape='line'
            listTheme='line'
            title={<FormattedMessage id='Autogen_common.r251126.s_Currency' />}
            items={toItems}
            className={css({
              flex: 1,
              '& > button': {
                justifyContent: 'center!',
              },
            })}
            onSelect={onSelectToCurrency}
          />
        </div>
        <ConversionLimitInfo
          convertibleLimit={convertibleLimit}
          toCurrency={convertibleLimitCurrency}
          toPrecision={convertiblePrecision}
        />
        <div className={cx(rowStyle, labelStyle)}>
          <p>
            <FormattedMessage id='Convert.r250401.Amount' />
          </p>
        </div>
        <div className={rowStyle}>
          <AmountInput
            name='fromAmount'
            precision={fromPrecision}
            className={amountInputStyle}
            placeholder={t('Convert.r250401.At_least_amount', { amount: fromMinAmount })}
            onChange={() => {
              triggerToAmount();
            }}
            onFocus={() => setFocusInput('fromAmount')}
            isFocused={focusInput === 'fromAmount'}
            maxLength={DefaultNumberInputMaxLength}
          />
          <span className={arrowStyle}>
            <SendIcon />
          </span>
          <AmountInput
            name='toAmount'
            precision={toPrecision}
            className={amountInputStyle}
            onChange={() => {
              triggerFromAmount();
            }}
            onFocus={() => setFocusInput('toAmount')}
            isFocused={focusInput === 'toAmount'}
            maxLength={DefaultNumberInputMaxLength}
          />
        </div>
        <RatioButtons onClick={(e) => setFormAmountPercent(e.currentTarget.value)} />
        <ExchangeRate
          exchangeRate={exchangeRate}
          fromCurrency={fromCurrency}
          toCurrency={toCurrency}
          fromPrecision={fromPrecision}
          toPrecision={toPrecision}
        />
        <div
          className={css({
            textStyle: 'Legacy/Body3',
            mt: 24,
            color: 'legacy.red',
            textAlign: 'center',
          })}
        >
          {errorMessage}
        </div>
        <footer>
          <Button
            onClick={close}
            label={<FormattedMessage id='Assets.r250401.Cancel' />}
            size='large'
            hierarchy='secondary'
            color='neutral'
          />
          <Button
            htmlType='submit'
            disabled={!isDirty || !isValid || isLoading}
            label={<FormattedMessage id='Assets.r250401.Convert' />}
            size='large'
          />
        </footer>
      </form>
      {currentPageWarningQuery.isSuccess && currentPageWarningQuery.data && (
        <CurrencyWarningAlert
          key={currentPageWarningQuery.data.blockchainName}
          warningData={currentPageWarningQuery.data}
        />
      )}
    </>
  );
};

const rowStyle = css({
  display: 'flex',
  gap: 8,
  w: '100%',
  '& > div, & > p': {
    flex: 1,
  },
});

const labelStyle = css({
  mb: 8,
});

const arrowStyle = flex({
  justify: 'center',
  align: 'center',
  color: 'legacy.gray.8',
  '& svg': {
    w: 16,
    transform: 'rotate(90deg)',
  },
});

const amountInputStyle = css({
  '& > input': {
    textAlign: 'center',
  },
  '& > input::placeholder': {
    textStyle: { base: 'Legacy/Body2', tablet: 'inherit' },
  },
});
